/**
 * User Search — inline search panel (not fullscreen overlay).
 * Stays within feed layout, 56px top offset for Telegram fullscreen.
 * Shows follow button for non-followed users.
 */

import { memo, useCallback, useRef, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Search, X, CheckCircle2, UserPlus, UserCheck, Clock, Lock } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useUserSearch } from '@turnifly/shared/hooks'
import { useFollowingSets } from '@turnifly/shared/hooks'
import { useToggleFollow } from '@turnifly/shared/hooks'
import type { SearchUser } from '@turnifly/shared/api'

interface UserSearchProps {
  userId: string
  isOpen: boolean
  onClose: () => void
}

const BADGE_ICON: Record<string, React.ReactNode> = {
  expert: <CheckCircle2 size={12} className="text-blue-400" />,
  organization: <CheckCircle2 size={12} className="text-emerald-400" />,
  company: <CheckCircle2 size={12} className="text-emerald-400" />,
}

export const UserSearch = memo(function UserSearch({
  userId, isOpen, onClose,
}: UserSearchProps): React.ReactElement | null {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const { query, setQuery, results, isSearching, clear } = useUserSearch(userId)
  const { data: followSets } = useFollowingSets(userId || undefined)
  const toggleFollow = useToggleFollow()
  const acceptedSet = followSets?.accepted ?? new Set<string>()
  const pendingSet = followSets?.pending ?? new Set<string>()

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    } else {
      clear()
    }
  }, [isOpen, clear])

  const handleSelect = useCallback((user: SearchUser): void => {
    navigate(`/profile/${user.id}`)
    onClose()
    clear()
  }, [navigate, onClose, clear])

  const handleClose = useCallback((): void => {
    onClose()
    clear()
  }, [onClose, clear])

  // Optimistic local follows
  const [localFollows, setLocalFollows] = useState<Set<string>>(new Set())

  const mergedFollowing = useMemo(() => {
    const merged = new Set(acceptedSet)
    for (const id of localFollows) merged.add(id)
    return merged
  }, [acceptedSet, localFollows])

  const handleFollow = useCallback((targetId: string, e: React.MouseEvent): void => {
    e.stopPropagation()
    if (!userId) return
    setLocalFollows((prev) => new Set(prev).add(targetId))
    toggleFollow.mutate({
      followerId: userId,
      followingId: targetId,
      isTargetPrivate: false,
      isCurrentlyFollowing: false,
    }, {
      onError: () => {
        setLocalFollows((prev) => {
          const next = new Set(prev)
          next.delete(targetId)
          return next
        })
      },
    })
  }, [userId, toggleFollow])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 bg-[var(--color-bg)] flex flex-col"
        >
          {/* Search header — 56px from top for Telegram fullscreen */}
          <div className="shrink-0 tg-header-safe-area">
            <div className="max-w-[600px] mx-auto flex items-center gap-3 px-4 py-2">
              <div className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                <Search size={16} className="text-text-hint/30 shrink-0" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('feed.search.placeholder')}
                  autoComplete="off"
                  className="flex-1 bg-transparent text-[14px] text-text placeholder:text-text-hint/30 focus:outline-none"
                />
                {query.length > 0 && (
                  <button
                    onClick={() => setQuery('')}
                    className="text-text-hint/40 tap-highlight-transparent"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <button
                onClick={handleClose}
                className="text-[14px] text-primary font-medium tap-highlight-transparent shrink-0"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto overscroll-y-contain">
            <div className="max-w-[600px] mx-auto">
              {isSearching && (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              {!isSearching && query.length >= 2 && results.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12">
                  <Search size={32} className="text-text-hint/15 mb-3" />
                  <p className="text-[14px] text-text-hint/40">{t('feed.search.noResults')}</p>
                </div>
              )}

              {results.length > 0 && results.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  isFollowing={mergedFollowing.has(user.id)}
                  isPending={pendingSet.has(user.id)}
                  isOwn={user.id === userId}
                  onSelect={handleSelect}
                  onFollow={handleFollow}
                />
              ))}

              {query.length < 2 && query.length > 0 && (
                <p className="text-center text-[13px] text-text-hint/30 py-8">
                  {t('feed.search.minChars')}
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
})

// ── User Row ──

function UserRow({ user, isFollowing, isPending, isOwn, onSelect, onFollow }: {
  user: SearchUser
  isFollowing: boolean
  isPending: boolean
  isOwn: boolean
  onSelect: (user: SearchUser) => void
  onFollow: (userId: string, e: React.MouseEvent) => void
}): React.ReactElement {
  const { t } = useTranslation()

  return (
    <button
      onClick={() => onSelect(user)}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors tap-highlight-transparent"
    >
      {/* Avatar with lock for private */}
      <div className="relative shrink-0">
        <div className="w-11 h-11 rounded-full overflow-hidden bg-white/[0.06] ring-1 ring-white/[0.06]">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[16px] font-semibold text-text-hint/50">
              {(user.full_name ?? '?')[0]}
            </div>
          )}
        </div>
        {user.is_private && !isFollowing && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[var(--color-bg)] flex items-center justify-center">
            <Lock size={9} className="text-text-hint/50" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-1.5">
          <span className="text-[14px] font-semibold text-text truncate">{user.full_name}</span>
          {user.badge_type && BADGE_ICON[user.badge_type]}
        </div>
        {user.username && (
          <p className="text-[12px] text-text-hint/40 truncate">@{user.username}</p>
        )}
        {!user.username && user.bio && (
          <p className="text-[12px] text-text-hint/40 truncate">{user.bio}</p>
        )}
      </div>

      {/* Follow state button */}
      {!isOwn && (
        isFollowing ? (
          <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/[0.04] shrink-0">
            <UserCheck size={13} className="text-text-hint/40" />
            <span className="text-[11px] text-text-hint/40 font-medium">{t('feed.search.following')}</span>
          </div>
        ) : isPending ? (
          <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/[0.04] shrink-0">
            <Clock size={12} className="text-text-hint/40" />
            <span className="text-[11px] text-text-hint/40 font-medium">{t('feed.pending')}</span>
          </div>
        ) : (
          <button
            onClick={(e) => onFollow(user.id, e)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary/10 text-primary shrink-0 hover:bg-primary/20 transition-colors"
          >
            <UserPlus size={13} />
            <span className="text-[11px] font-semibold">{t('feed.follow')}</span>
          </button>
        )
      )}
    </button>
  )
}
